// Which locations the "combine" pages are showing (Finance, Marketing home and
// calendar, Performance, Campaigns). ONE selection for the whole app, so the
// numbers don't change under you as you move between them.
//
// Rules (unchanged from Finance's filter, 19 Sept):
//   - defaults to the location this device is working at;
//   - remembered for the tab only (sessionStorage);
//   - cleared whenever the device switches location (switchToSite and the
//     switch guard both call clearViewing), so a page never shows the old
//     site's figures under the new site;
//   - a remembered site that is no longer theirs is dropped;
//   - sent to the API only when it is a real change from "this location",
//     so a single-site salon's requests never change.
import { useEffect, useState } from 'react';
import useSites from './useSites';

const KEY = 'loc:viewing';
// The per-page keys this replaced; cleared alongside it so an old tab can't
// bring a stale selection back.
const OLD_KEYS = ['fin:salonIds', 'mkt:salonIds']; // keep in step with sites.service VIEWING_KEYS
const here = () => String(localStorage.getItem('salonId') || '');

const read = () => {
  try {
    const saved = JSON.parse(sessionStorage.getItem(KEY) || 'null');
    if (Array.isArray(saved) && saved.length) return saved.map(String);
  } catch (e) { /* ignore */ }
  return here() ? [here()] : [];
};

let current = null;
const listeners = new Set();
export const getViewing = () => { if (!current) current = read(); return current; };
export const setViewing = (ids) => {
  current = (ids || []).map(String);
  try { sessionStorage.setItem(KEY, JSON.stringify(current)); } catch (e) { /* ignore */ }
  listeners.forEach((fn) => fn(current));
};

/** Forget the selection: the next read starts again at "this location". */
export const clearViewing = () => {
  current = null;
  [KEY, ...OLD_KEYS].forEach((k) => { try { sessionStorage.removeItem(k); } catch (e) { /* ignore */ } });
};

// What a request sends: null unless it spans more than this location.
export const salonIdsParam = (ids) => {
  const list = ids || getViewing();
  if (!list.length || (list.length === 1 && list[0] === here())) return null;
  return list.join(',');
};

// Another site's item: can be looked at, not opened or changed from here.
export const isRemote = (salonId) => !!salonId && String(salonId) !== here();

/**
 * The selection, for a page that can combine locations. `enabled` is false for
 * a single-site salon and for anyone who can't see more than their own
 * location's figures; then `param` is always null.
 */
export function useViewingSites() {
  const { sites, activeSalonId, multi, isOwnerHere } = useSites();
  const [ids, setIds] = useState(getViewing);
  useEffect(() => {
    const fn = (next) => setIds(next);
    listeners.add(fn);
    return () => { listeners.delete(fn); };
  }, []);
  useEffect(() => {
    if (!sites || !sites.length) return;
    const valid = sites.map((s) => String(s.salonId));
    const kept = getViewing().filter((id) => valid.includes(id));
    if (kept.length !== getViewing().length) setViewing(kept.length ? kept : (activeSalonId ? [String(activeSalonId)] : []));
  }, [sites, activeSalonId]);
  const enabled = !!(multi && isOwnerHere);
  const param = enabled ? salonIdsParam(ids) : null;
  const selectedSites = (sites || []).filter((s) => ids.includes(String(s.salonId)));
  return { salonIds: ids, setSalonIds: setViewing, param, spans: !!param, count: param ? ids.length : 1, selectedSites, enabled };
}

// Tests only.
export const _reset = () => { current = null; listeners.clear(); };
